interface SeoOptions {
  // Omit on the home page so the full brand title (seo.defaultTitle) is used instead of "Home | Echopus".
  title?: string
  description?: string
  image?: string
  imageAlt?: string
  noindex?: boolean
  type?: 'website' | 'article'
  // Last breadcrumb label; defaults to `title`. Every page except home gets Home › <page>.
  breadcrumb?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

// Pass a getter so titles, descriptions and JSON-LD follow in-place language switches
// (URLs have no locale prefix, so the page is not re-created when the locale changes).
export function useSiteSeo(options: SeoOptions | (() => SeoOptions) = {}) {
  const route = useRoute()
  const config = useRuntimeConfig()
  const appConfig = useAppConfig()
  const { locale, t } = useI18n()

  const site = appConfig.site
  const siteUrl = config.public.siteUrl as string

  const localeMap: Record<string, string> = {
    en: 'en_US',
    ar: 'ar_SA',
  }

  const seo = computed(() => {
    const opts = typeof options === 'function' ? options() : options
    const path = route.path === '/' ? '' : route.path.replace(/\/+$/, '')
    const title = opts.title ? `${opts.title} | ${site.name}` : t('seo.defaultTitle')
    // Pages are served with a trailing slash (/about/); the sitemap uses the same form.
    const canonical = `${siteUrl}${path}/`
    const jsonLd = opts.jsonLd ? (Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd]) : []

    const crumb = opts.breadcrumb || opts.title
    if (path && crumb && !opts.noindex) {
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('nav.home'), item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: crumb, item: canonical },
        ],
      })
    }

    return {
      title,
      description: opts.description || t('seo.defaultDescription'),
      image: opts.image || `${siteUrl}${site.defaultOgImage}`,
      imageAlt: opts.imageAlt || t('seo.defaultTitle'),
      canonical,
      type: opts.type || 'website',
      robots: opts.noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      jsonLd,
    }
  })

  useSeoMeta({
    title: () => seo.value.title,
    description: () => seo.value.description,
    ogTitle: () => seo.value.title,
    ogDescription: () => seo.value.description,
    ogType: () => seo.value.type,
    // Not-found pages (noindex) have no canonical URL of their own.
    ogUrl: () => (seo.value.robots.startsWith('noindex') ? undefined : seo.value.canonical),
    ogImage: () => seo.value.image,
    ogImageAlt: () => seo.value.imageAlt,
    ogSiteName: site.name,
    ogLocale: () => localeMap[locale.value] || site.locale,
    twitterCard: 'summary_large_image',
    twitterSite: site.twitter,
    twitterTitle: () => seo.value.title,
    twitterDescription: () => seo.value.description,
    twitterImage: () => seo.value.image,
    twitterImageAlt: () => seo.value.imageAlt,
    robots: () => seo.value.robots,
  })

  useHead(() => ({
    link: seo.value.robots.startsWith('noindex') ? [] : [{ rel: 'canonical', href: seo.value.canonical }],
    script: seo.value.jsonLd.map((schema) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    })),
  }))
}

// Site-wide Organization + WebSite graph. The WebSite node (name + alternateName) is what Google
// reads for the site name shown above results; the Organization logo feeds the brand/knowledge panel.
export function useOrganizationSchema() {
  const config = useRuntimeConfig()
  const siteUrl = config.public.siteUrl as string
  const { t, locale } = useI18n()

  // Returns a getter so the description re-translates on a language switch.
  return () => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Echopus',
        alternateName: ['إيكوبوس', 'Echopus Studio'],
        url: `${siteUrl}/`,
        logo: {
          '@type': 'ImageObject',
          '@id': `${siteUrl}/#logo`,
          url: `${siteUrl}/icon-512.png`,
          contentUrl: `${siteUrl}/icon-512.png`,
          width: 512,
          height: 512,
          caption: 'Echopus',
        },
        image: { '@id': `${siteUrl}/#logo` },
        description: t('seo.organizationDescription'),
        foundingDate: '2023',
        sameAs: [
          'https://www.facebook.com/echopus',
          'https://www.instagram.com/echoopus/',
          'https://www.linkedin.com/company/echopus/',
          'https://www.tiktok.com/@echoopus',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          url: `${siteUrl}/contact/`,
          availableLanguage: ['English', 'Arabic'],
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: 'Echopus',
        alternateName: ['إيكوبوس', 'echopus.com'],
        url: `${siteUrl}/`,
        description: t('seo.home.siteDescription'),
        inLanguage: locale.value === 'ar' ? 'ar-SA' : 'en-US',
        publisher: { '@id': `${siteUrl}/#organization` },
      },
    ],
  })
}
